/**
 * BasicComparisonTable Component
 *
 * Displays before/after BASIC scores comparison for merger analysis.
 * Shows change indicators with color coding.
 */

import React from 'react';
import {
  TrendingUp,
  TrendingDown,
  Minus,
  AlertTriangle,
  CheckCircle,
  AlertCircle,
} from 'lucide-react';
import type { CombinedBasics, CarrierSummary } from '../../services/merger.api';

interface BasicComparisonTableProps {
  carriers: CarrierSummary[];
  combinedBasics: CombinedBasics | Record<string, any>;
  showIndividualScores?: boolean;
}

// BASIC category display names and IDs
// key: frontend display key, basicId: backend key format (BASIC-XX), label: display name
const BASIC_CATEGORIES = [
  { key: 'Unsafe Driving', basicId: 'BASIC-11', label: 'Unsafe Driving' },
  { key: 'HOS Compliance', basicId: 'BASIC-12', label: 'Hours-of-Service' },
  { key: 'Driver Fitness', basicId: 'BASIC-13', label: 'Driver Fitness' },
  { key: 'Controlled Substances', basicId: 'BASIC-14', label: 'Controlled Substances' },
  { key: 'Vehicle Maintenance', basicId: 'BASIC-15', label: 'Vehicle Maintenance' },
  { key: 'Crash Indicator', basicId: 'BASIC-16', label: 'Crash Indicator', optional: true },
  { key: 'Hazmat Compliance', basicId: 'BASIC-17', label: 'Hazmat Compliance', optional: true },
];

// Get status badge component
const StatusBadge: React.FC<{ status: string }> = ({ status }) => {
  const config: Record<string, { bg: string; color: string; icon: React.ElementType }> = {
    // Frontend expected statuses
    OK: { bg: '#d1e6d9', color: '#4b5c3a', icon: CheckCircle },
    WARNING: { bg: '#f1e2c2', color: '#664911', icon: AlertCircle },
    ALERT: { bg: '#f2dad2', color: '#7a2b1e', icon: AlertTriangle },
    // Backend statuses (from PredictionStatus)
    EXCELLENT: { bg: '#d1e6d9', color: '#4b5c3a', icon: CheckCircle },
    GOOD: { bg: '#d1e6d9', color: '#4b5c3a', icon: CheckCircle },
    CRITICAL: { bg: '#f2dad2', color: '#7a2b1e', icon: AlertTriangle },
    INSUFFICIENT: { bg: '#efebe1', color: '#857f73', icon: AlertCircle },
  };

  const { bg, color, icon: Icon } = config[status] || config.OK;

  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: '0.25rem',
        padding: '0.25rem 0.5rem',
        borderRadius: 'var(--radius-md)',
        backgroundColor: bg,
        color: color,
        fontSize: '0.75rem',
        fontWeight: 500,
      }}
    >
      <Icon style={{ width: 12, height: 12 }} />
      {status}
    </span>
  );
};

// Get change indicator component
const ChangeIndicator: React.FC<{ change: number; isInverse?: boolean }> = ({
  change,
  isInverse = false,
}) => {
  // For BASIC scores, higher is worse, so positive change is bad
  // Unless isInverse is true (for metrics where higher is better)
  const isPositive = isInverse ? change > 0 : change < 0;
  const isNeutral = Math.abs(change) < 1;

  if (isNeutral) {
    return (
      <span
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: '0.25rem',
          color: 'var(--color-gray-500)',
          fontSize: '0.8125rem',
        }}
      >
        <Minus style={{ width: 14, height: 14 }} />
        No change
      </span>
    );
  }

  const color = isPositive ? '#5d7148' : '#b3412c';
  const Icon = change > 0 ? TrendingUp : TrendingDown;
  const sign = change > 0 ? '+' : '';

  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: '0.25rem',
        color: color,
        fontSize: '0.8125rem',
        fontWeight: 500,
      }}
    >
      <Icon style={{ width: 14, height: 14 }} />
      {sign}
      {change.toFixed(1)}%
    </span>
  );
};

// Percentile bar component
const PercentileBar: React.FC<{ percentile: number; threshold: number }> = ({
  percentile,
  threshold,
}) => {
  const isAboveThreshold = percentile >= threshold;
  const barColor = isAboveThreshold ? '#b3412c' : percentile >= threshold * 0.8 ? '#b07d2b' : '#5d7148';

  return (
    <div style={{ width: '100%', maxWidth: 120 }}>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          marginBottom: '0.25rem',
          fontSize: '0.75rem',
        }}
      >
        <span style={{ fontWeight: 600, color: barColor }}>{percentile.toFixed(0)}%</span>
        <span style={{ color: 'var(--color-gray-400)' }}>/{threshold}%</span>
      </div>
      <div
        style={{
          height: 6,
          backgroundColor: 'var(--color-gray-200)',
          borderRadius: 3,
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        {/* Threshold marker */}
        <div
          style={{
            position: 'absolute',
            left: `${threshold}%`,
            top: 0,
            bottom: 0,
            width: 2,
            backgroundColor: 'var(--color-gray-400)',
            zIndex: 1,
          }}
        />
        {/* Percentile bar */}
        <div
          style={{
            height: '100%',
            width: `${Math.min(percentile, 100)}%`,
            backgroundColor: barColor,
            borderRadius: 3,
            transition: 'width 0.5s ease',
          }}
        />
      </div>
    </div>
  );
};

const BasicComparisonTable: React.FC<BasicComparisonTableProps> = ({
  carriers,
  combinedBasics,
  showIndividualScores = true,
}) => {
  // Helper to get carrier BASIC score - handles both frontend and backend data formats
  const getCarrierBasicScore = (carrier: CarrierSummary, category: typeof BASIC_CATEGORIES[0]): number | null => {
    // Backend sends data as 'basics' with BASIC-XX keys
    const carrierAny = carrier as any;
    const basics = carrierAny.basics || carrier.basicScores;
    if (!basics) return null;

    // Try backend format first (BASIC-XX keys with 'percentile' property)
    if (basics[category.basicId]) {
      const basic = basics[category.basicId];
      if (basic.percentile !== undefined && basic.percentile !== null) {
        return basic.percentile;
      }
    }

    // Try frontend format (display name keys like 'Unsafe Driving')
    if (basics[category.key]) {
      const basic = basics[category.key];
      if (basic.percentile !== undefined && basic.percentile !== null) {
        return basic.percentile;
      }
    }

    return null;
  };

  // Helper to get combined BASIC score - handles backend format
  const getCombinedBasicScore = (category: typeof BASIC_CATEGORIES[0]): {
    percentile: number;
    threshold: number;
    status: string;
    change: number;
    measure: number;
    previousAvg: number;
  } | null => {
    if (!combinedBasics) return null;

    const combinedAny = combinedBasics as any;

    // Try backend format (BASIC-XX keys with combinedPercentile)
    let score = combinedAny[category.basicId];
    if (!score) {
      // Try frontend format (display name keys)
      score = combinedAny[category.key];
    }

    if (!score) return null;

    // Normalize to frontend expected format
    // Backend sends: combinedPercentile, combinedMeasure, beforeAvg, status
    // Frontend expects: percentile, measure, previousAvg, status, change, threshold
    return {
      percentile: score.combinedPercentile ?? score.percentile ?? 0,
      threshold: score.threshold ?? 65,
      status: score.status || 'OK',
      change: score.change ?? 0,
      measure: score.combinedMeasure ?? score.measure ?? 0,
      previousAvg: score.beforeAvg ?? score.previousAvg ?? 0,
    };
  };

  return (
    <div className="card-modern">
      <div style={{ marginBottom: 'var(--space-lg)' }}>
        <h3
          className="heading-3"
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 'var(--space-sm)',
          }}
        >
          BASIC Scores Comparison
        </h3>
        <p className="text-muted" style={{ marginTop: 'var(--space-xs)' }}>
          Before and after merger BASIC percentiles. Higher percentiles indicate worse performance.
        </p>
      </div>

      {/* Desktop Table */}
      <div style={{ overflowX: 'auto' }}>
        <table className="table-modern" style={{ minWidth: showIndividualScores ? 800 : 600 }}>
          <thead>
            <tr>
              <th style={{ width: '180px' }}>BASIC Category</th>
              {showIndividualScores &&
                carriers.map((carrier, index) => (
                  <th key={carrier.dotNumber} style={{ width: '100px', textAlign: 'center' }}>
                    <div style={{ fontSize: '0.75rem', color: 'var(--color-gray-500)' }}>
                      Carrier {String.fromCharCode(65 + index)}
                    </div>
                    <div
                      style={{
                        fontSize: '0.75rem',
                        fontWeight: 400,
                        color: 'var(--color-gray-400)',
                        maxWidth: 100,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      DOT {carrier.dotNumber}
                    </div>
                  </th>
                ))}
              <th style={{ width: '100px', textAlign: 'center' }}>
                <div style={{ color: 'var(--color-gray-500)' }}>Average</div>
                <div
                  style={{
                    fontSize: '0.75rem',
                    fontWeight: 400,
                    color: 'var(--color-gray-400)',
                  }}
                >
                  Before
                </div>
              </th>
              <th style={{ width: '150px', textAlign: 'center' }}>
                <div style={{ color: 'var(--color-primary)' }}>Combined</div>
                <div
                  style={{
                    fontSize: '0.75rem',
                    fontWeight: 400,
                    color: 'var(--color-gray-400)',
                  }}
                >
                  After Merger
                </div>
              </th>
              <th style={{ width: '120px', textAlign: 'center' }}>Change</th>
              <th style={{ width: '80px', textAlign: 'center' }}>Status</th>
            </tr>
          </thead>
          <tbody>
            {BASIC_CATEGORIES.map((category) => {
              // Get combined score using helper function that handles backend format
              const combinedScore = getCombinedBasicScore(category);

              // Skip optional categories if no data
              if (category.optional && !combinedScore) {
                return null;
              }

              // Calculate average of individual carrier scores
              const individualScores = carriers
                .map((c) => getCarrierBasicScore(c, category))
                .filter((s): s is number => s !== null);
              const avgBefore =
                individualScores.length > 0
                  ? individualScores.reduce((a, b) => a + b, 0) / individualScores.length
                  : null;

              // Use combined score or create default
              const combined = combinedScore || {
                percentile: avgBefore || 0,
                threshold: 65,
                status: 'OK' as const,
                change: 0,
                measure: 0,
                previousAvg: avgBefore || 0,
              };

              return (
                <tr key={category.key}>
                  <td>
                    <div style={{ fontWeight: 500, color: 'var(--color-gray-900)' }}>
                      {category.label}
                    </div>
                    <div style={{ fontSize: '0.75rem', color: 'var(--color-gray-400)' }}>
                      {category.basicId}
                    </div>
                  </td>

                  {showIndividualScores &&
                    carriers.map((carrier) => {
                      const score = getCarrierBasicScore(carrier, category);
                      return (
                        <td key={carrier.dotNumber} style={{ textAlign: 'center' }}>
                          {score !== null ? (
                            <span
                              style={{
                                fontWeight: 600,
                                color:
                                  score >= combined.threshold
                                    ? '#b3412c'
                                    : score >= combined.threshold * 0.8
                                    ? '#b07d2b'
                                    : 'var(--color-gray-700)',
                              }}
                            >
                              {score.toFixed(0)}%
                            </span>
                          ) : (
                            <span style={{ color: 'var(--color-gray-400)' }}>N/A</span>
                          )}
                        </td>
                      );
                    })}

                  <td style={{ textAlign: 'center' }}>
                    {avgBefore !== null ? (
                      <span
                        style={{
                          fontWeight: 500,
                          color: 'var(--color-gray-600)',
                        }}
                      >
                        {avgBefore.toFixed(0)}%
                      </span>
                    ) : (
                      <span style={{ color: 'var(--color-gray-400)' }}>N/A</span>
                    )}
                  </td>

                  <td>
                    <PercentileBar
                      percentile={combined.percentile}
                      threshold={combined.threshold}
                    />
                  </td>

                  <td style={{ textAlign: 'center' }}>
                    <ChangeIndicator change={combined.change} />
                  </td>

                  <td style={{ textAlign: 'center' }}>
                    <StatusBadge status={combined.status} />
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Legend */}
      <div
        style={{
          marginTop: 'var(--space-lg)',
          paddingTop: 'var(--space-md)',
          borderTop: '1px solid var(--color-gray-200)',
          display: 'flex',
          flexWrap: 'wrap',
          gap: 'var(--space-lg)',
          fontSize: '0.75rem',
          color: 'var(--color-gray-500)',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-xs)' }}>
          <div
            style={{
              width: 12,
              height: 12,
              borderRadius: 2,
              backgroundColor: '#5d7148',
            }}
          />
          Below Threshold (Safe)
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-xs)' }}>
          <div
            style={{
              width: 12,
              height: 12,
              borderRadius: 2,
              backgroundColor: '#b07d2b',
            }}
          />
          Approaching Threshold
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-xs)' }}>
          <div
            style={{
              width: 12,
              height: 12,
              borderRadius: 2,
              backgroundColor: '#b3412c',
            }}
          />
          Above Threshold (Alert)
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-xs)' }}>
          <div
            style={{
              width: 2,
              height: 12,
              backgroundColor: 'var(--color-gray-400)',
            }}
          />
          Intervention Threshold
        </div>
      </div>
    </div>
  );
};

export default BasicComparisonTable;
